<template>
  <div class="agentic-dashboard-wrapper">
    <ErrorPopup />
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div class="dashboard-header-left">
        <div class="dash-top-center">
          <input 
            type="text" 
            v-model="dashboardName" 
            :style="{ width: inputWidth + 'ch' }" 
            placeholder="untitled"
            :readonly="!isDashboardCreator"
            @change="handleDashboardNameChange"
            class="dashboard-name-input"
          />
        </div>
      </div>
      <div class="dashboard-header-right">
        <div v-if="isDashboardCreator" class="tab-div dash-access-toggle justify-space-between">
          <v-btn-toggle
            v-model="isPublic"
            class="axis-toggle font-overlay"
            mandatory
            @update:modelValue="handleDashAccessToggleChange"
          >
            <v-btn :value="false" class="switch-btn" outlined>My Dashboard</v-btn>
            <v-btn :value="true" class="switch-btn" outlined>Shared</v-btn>
          </v-btn-toggle>
        </div>
        <button 
          class="chat-toggle-btn" 
          @click="toggleChat"
          :class="{ 'active': chatVisible }"
          :title="chatVisible ? 'Hide Chat' : 'Show Chat'"
        >
          <img :src="chatIcon" alt="chat" />
        </button>
        <button 
          v-if="isDashboardCreator"
          class="dash-save" 
          @click="saveDashboard"
          :disabled="!isDashboardNameChanged || saving"
          :class="{ 'disabled': !isDashboardNameChanged || saving }"
        >
          <img :src="saveIcon" alt="save" />
        </button>
        <button v-if="isDashboardCreator" class="dash-delete" @click="showDeleteDialog = true">
          <img :src="deleteIcon" alt="delete" />
        </button>
      </div>
    </div>

    <div
      class="agentic-dashboard-content"
      :class="{
        'plot-selected': selectedChartIndex !== null,
        'chat-collapsed': !chatVisible && !showVisualEditPanel,
      }"
    >
      <!-- Left side: Selected plot view (when plot is selected) -->
      <div v-if="selectedChartIndex !== null && charts[selectedChartIndex] && !charts[selectedChartIndex].loading && !charts[selectedChartIndex].error" class="selected-plot-panel" :class="{ 'visual-edit-active': showVisualEditPanel }">
        <div class="selected-plot-header">
          <div class="selected-plot-title-wrap">
            <v-icon
              v-if="isSharedPublicPlot(selectedChartIndex)"
              class="shared-plot-icon"
              size="16"
              title="Shared — this plot was created by another user and cannot be visually edited"
            >mdi-earth</v-icon>
            <h6 class="selected-plot-title">{{ getChartTitle(selectedChartIndex) }}</h6>
          </div>
          <div class="selected-plot-actions">
            <button class="close-plot-btn" @click="deselectPlot" title="Close">
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="selected-plot-content">
          <ChartCard
            ref="selectedPlotChartCard"
            :chart-title="getChartTitle(selectedChartIndex)"
            :chart-data="charts[selectedChartIndex]"
            :chart-index="selectedChartIndex"
            :is-selected="false"
            :full-screen="false"
            :hide-header="true"
          />
        </div>
      </div>

      <!-- Main dashboard section with charts grid (6 slots) - shown when no plot is selected -->
      <div v-if="selectedChartIndex === null" class="dashboard-main-section">
        <div class="dashboard-inner" style="position: relative;">
          <DashboardGrid
            :charts="charts"
            :selected-chart-index="selectedChartIndex"
            @chart-selected="handleChartSelected"
            @empty-slot-click="handleEmptySlotClick"
          />
        </div>
      </div>
      
      <!-- Right sidebar with chat or visual edit -->
      <div class="dashboard-sidebar" :class="{ 'hidden': !chatVisible && !showVisualEditPanel }">
        <!-- Chat Sidebar -->
        <ChatSidebar
          v-if="!showVisualEditPanel"
          ref="chatSidebar"
          :messages="currentChatMessages"
          :initial-message="initialMessage"
          :ai-response="aiResponse"
          :selected-chart-index="selectedChartIndex"
          :loading="loadingChatMessage"
          :can-edit="isDashboardCreator"
          @message-like="handleMessageLike"
          @message-dislike="handleMessageDislike"
          @send-message="handleChatMessage"
          @visual-edit="handleVisualEdit"
        />
        <!-- Visual Edit Panel (replaces chat) -->
        <div v-if="showVisualEditPanel && selectedChartIndex !== null && isDashboardCreator" class="visual-edit-sidebar">
          <div class="visual-edit-sidebar-header">
            <h6 class="visual-edit-sidebar-title">Visual Edit</h6>
            <div class="visual-edit-sidebar-actions">
              <button class="switch-to-chat-btn" @click="switchToChat" title="Switch to Chat">
                <v-icon size="20">mdi-message-text</v-icon>
              </button>
            </div>
          </div>
          <div class="visual-edit-sidebar-content">
            <VisualEditsPanel
              v-if="charts[selectedChartIndex]"
              :key="`visual-edit-${selectedChartIndex}`"
              :selected-chart="charts[selectedChartIndex]"
              @plot-updated="handlePlotUpdated"
              @close="exitEditMode"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      transition="dialog-top-transition"
      class="save-dashboard"
      width="600"
      v-model="showDeleteDialog"
    >
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-title class="d-flex align-center justify-between">
            <v-spacer></v-spacer>
            <v-btn
              @click="showDeleteDialog = false"
              flat
              icon
              class="chart-preview-close-btn"
              size="x-small"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="mt-4 mx-5">
            <h6 class="text-center">Delete Dashboard</h6>
            <p class="text-center">
              Are you sure you want to delete dashboard?
            </p>
          </v-card-text>
          <v-card-actions class="btn-wrap">
            <v-btn class="cancel-btn" @click="isActive.value = false">
              <span>Cancel</span>
            </v-btn>
            <v-btn
              class="delete-btn"
              @click="confirmDelete"
              :loading="deleting"
            >
              <img class="mr-2" :src="deleteDashboardPopup" width="15px" height="15px" alt="delete_dashboard" />
              <span>Delete</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import DashboardGrid from "@/components/AgenticDashboard/DashboardGrid.vue";
import ChartCard from "@/components/AgenticDashboard/ChartCard.vue";
import ChatSidebar from "@/components/AgenticDashboard/ChatSidebar.vue";
import VisualEditsPanel from "@/components/AgenticDashboard/VisualEditsPanel.vue";
import AgenticSidebar from "@/components/AgenticDashboard/AgenticSidebar.vue";
import ErrorPopup from "@/components/layout/Popups/ErrorPopup.vue";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { mapGetters } from "vuex";
import {
  parsePlotViewZoomChatMessage,
  describePlotViewZoomReply,
} from "@/utils/plotChatViewCommands";
import Plotly from "plotly.js-dist-min";
import { eventBus } from "@/eventBus";

export default {
  name: "AgenticDashboardView",
  components: {
    DashboardGrid,
    ChartCard,
    ChatSidebar,
    VisualEditsPanel,
    ErrorPopup,
  },
  props: {
    dashboardId: {
      type: [String, Number],
      default: null,
    },
    dashboardName: {
      type: String,
      default: null,
    },
    initialMessage: {
      type: String,
      default: null,
    },
    dashboardData: {
      type: Object,
      default: null,
    },
  },
  data() {
    const routeName = this.$route?.params?.dashboardName;
    let initialName = "Untitled Dashboard";
    if (routeName) {
      try {
        initialName = decodeURIComponent(routeName);
      } catch (e) {
        initialName = routeName;
      }
    }
    
    return {
      charts: Array(6).fill(null), // 6 slots for plots
      dashboardName: initialName,
      originalDashboardName: initialName, // Track original name to detect changes
      dashboardIdValue: null, // Store the actual dashboard ID
      chatVisible: true, // Chat sidebar visibility toggle
      showDeleteDialog: false,
      deleting: false,
      saving: false,
      selectedChartIndex: null, // Currently selected chart index
      showVisualEditPanel: false, // Show visual edit panel (replaces chat)
      deleteIcon: require("@/assets/Svg/trash.svg"),
      deleteDashboardPopup: require("@/assets/Svg/Chart/delete-white.svg"),
      saveIcon: require("@/assets/Svg/save.svg"),
      chatIcon: require("@/assets/Images/FDPal/send-2.svg"), // Chat toggle icon
      aiResponse: null,
      loadingChatMessage: false, // Loading state for chat messages
      // Session-only chat history: key = "dashboard" or chart ID; cleared on refresh/leave
      chatHistories: {},
      dashboardAccessLevel: "private", // Default access level
      isPublic: false,
      dashboardCreatedBy: "",
      isDefault: false, // Default dashboard flag
    };
  },
  computed: {
    ...mapGetters("tenantId", ["getUser"]),
    tenantId() {
      return this.$route.path.split("/")[1] || getTenantPrefix();
    },
    inputWidth() {
      return this.dashboardName.length > 0 ? this.dashboardName.length : 15;
    },
    // Key for current chat context: "dashboard" or selected chart ID
    currentChatKey() {
      if (this.selectedChartIndex !== null && this.charts[this.selectedChartIndex]) {
        const chart = this.charts[this.selectedChartIndex];
        const id = chart?.data?.ID || chart?.ID;
        return id ? String(id) : "dashboard";
      }
      return "dashboard";
    },
    // Messages for the current context (dashboard or single-plot chat); persists for session only
    currentChatMessages() {
      const list = this.chatHistories[this.currentChatKey];
      return Array.isArray(list) ? list : [];
    },
    currentDashboardId() {
      if (this.dashboardIdValue) {
        return this.dashboardIdValue;
      }
      if (this.dashboardData && (this.dashboardData.ID || this.dashboardData.id)) {
        return this.dashboardData.ID || this.dashboardData.id;
      }
      if (this.$route.params.dashboardId) {
        return this.$route.params.dashboardId;
      }
      if (this.dashboardId) {
        return this.dashboardId;
      }
      return null;
    },
    isDashboardNameChanged() {
      return this.dashboardName.trim() !== this.originalDashboardName.trim() && 
             this.dashboardName.trim() !== "";
    },
    isDashboardCreator() {
      const userEmail = this.getUser?.username;
      return Boolean(userEmail && this.dashboardCreatedBy && this.dashboardCreatedBy === userEmail);
    },
  },
  watch: {
    isDashboardCreator(isCreator) {
      if (!isCreator) {
        this.showVisualEditPanel = false;
      }
    },
    '$route.params.dashboardName'(newName, oldName) {
      if (newName && newName !== oldName) {
        console.log("Dashboard name changed in route, loading dashboard:", newName);
        this.initializeDashboard();
      }
    },
    dashboardId(newId, oldId) {
      if (newId && newId !== oldId) {
        console.log("Dashboard ID prop changed, loading dashboard:", newId);
        this.initializeDashboard();
      }
    },
    chatVisible(newVal, oldVal) {
      if (newVal !== oldVal) {
        setTimeout(() => {
          this.resizeAllCharts();
        }, 350); // Slightly longer than CSS transition (300ms)
      }
    },
    // Seed dashboard chat history with initial message when present (e.g. from home page)
    initialMessage: {
      handler(val) {
        if (!val || this.currentChatKey !== "dashboard") return;
        this.ensureChatHistory("dashboard");
        const list = this.chatHistories.dashboard;
        const exists = list.some((m) => m.role === "user" && m.text === val);
        if (!exists) list.push({ role: "user", text: val });
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeDashboard();
  },
  methods: {
    applyDashboardMeta(dashboard) {
      this.dashboardAccessLevel = dashboard.AccessLevel || "private";
      this.isPublic = this.dashboardAccessLevel === "public";
      this.isDefault = dashboard.isDefault || false;
      this.dashboardCreatedBy = dashboard.createdBy || dashboard.CreatedBy || "";
    },
    initializeDashboard() {
    // Check if we have dashboard data from API response (from prop or query params)
    const dashboardData = this.dashboardData || (() => {
      const dashboardDataStr = this.$route.query.dashboardData;
      if (dashboardDataStr) {
        try {
          return JSON.parse(dashboardDataStr);
        } catch (error) {
          console.error("Error parsing dashboard data:", error);
          return null;
        }
      }
      return null;
    })();

      let dashboardName = this.$route.params.dashboardName || this.dashboardName;
      const dashboardId = this.dashboardId || this.$route.params.dashboardId;

    if (dashboardData) {
      this.handleDashboardApiResponse(dashboardData);
      } else if (dashboardName) {
        if (this.$route.params.dashboardName) {
          try {
            dashboardName = decodeURIComponent(this.$route.params.dashboardName);
          } catch (e) {
            dashboardName = this.$route.params.dashboardName;
          }
        } else {
          try {
            dashboardName = decodeURIComponent(dashboardName);
          } catch (e) {
            void 0;
          }
        }
        console.log("Loading dashboard from name (from URL):", dashboardName);
        console.log("Route params dashboardName (raw):", this.$route.params.dashboardName);
        console.log("Current dashboardName in data:", this.dashboardName);
        this.loadDashboardByName(dashboardName);
      } else if (dashboardId) {
        console.log("Loading dashboard from ID:", dashboardId);
        this.loadDashboard(dashboardId);
    } else if (this.initialMessage) {
      // If we have an initial message but no dashboard ID, create a new dashboard
      this.createDashboardFromMessage(this.initialMessage);
    }
  },
    async loadDashboardByName(name) {
      if (!name) {
        console.error("Dashboard name is required");
        this.$store.commit('api/showSnackbar', {
          message: "Dashboard name is missing",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        const jsonStringifiedName = JSON.stringify(name);
        const encodedName = encodeURIComponent(jsonStringifiedName);
        console.log("Fetching dashboard with name:", name);
        console.log("JSON stringified:", jsonStringifiedName);
        console.log("URL encoded:", encodedName);
        console.log("Full API URL:", `public/api/dashboards?name=${encodedName}`);
        const response = await api.get(`public/api/dashboards?name=${encodedName}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          const dashboards = response.data.data.dashboards || [];
          if (dashboards.length === 0) {
            throw new Error("Dashboard not found");
          }
          
          const dashboard = dashboards[0];
          const fetchedName = dashboard.name || dashboard.Name || "Untitled Dashboard";
          
          this.dashboardIdValue = dashboard.ID || dashboard.id || null;
          
          this.dashboardName = fetchedName;
          this.originalDashboardName = fetchedName; // Set original name
          this.applyDashboardMeta(dashboard);

          const currentName = this.$route.params.dashboardName;
          if (currentName !== this.dashboardName) {
            this.$router.replace({
              name: "AgenticDashboardView",
              params: { dashboardName: this.dashboardName },
            });
          }

          if (dashboard.ChartsID && dashboard.ChartsID.length > 0) {
            await this.loadChartsFromIds(dashboard.ChartsID);
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard loaded successfully",
              color: 'success',
              timeout: 3000
            });
          } else {
            this.charts = Array(6).fill(null);
          }
        } else {
          throw new Error(response?.data?.message || "Failed to load dashboard");
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to load dashboard",
          color: 'error',
          timeout: 5000
        });
      }
    },
    async loadDashboard(id) {
      if (!id) {
        console.error("Dashboard ID is required");
        this.$store.commit('api/showSnackbar', {
          message: "Dashboard ID is missing",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        const response = await api.get(`public/api/dashboards/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            const dashboard = response.data.data.dashboards[0];
          
          this.dashboardIdValue = dashboard.ID || dashboard.id || null;
          
          this.dashboardName = dashboard.name || dashboard.Name || "Untitled Dashboard";
          this.originalDashboardName = this.dashboardName; // Set original name
          this.applyDashboardMeta(dashboard);

          this.$router.replace({
            name: "AgenticDashboardView",
            params: { dashboardName: this.dashboardName },
          });

          if (dashboard.ChartsID && dashboard.ChartsID.length > 0) {
            await this.loadChartsFromIds(dashboard.ChartsID);
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard loaded successfully",
              color: 'success',
              timeout: 3000
            });
          } else {
            this.charts = Array(6).fill(null);
          }
        } else {
          throw new Error(response?.data?.message || "Failed to load dashboard");
        }
      } catch (error) {
          console.error("Error loading dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to load dashboard",
          color: 'error',
          timeout: 5000
        });
      }
    },
    async handleDashboardApiResponse(dashboardData) {
      this.dashboardIdValue = dashboardData.ID || dashboardData.id || null;
      
      this.dashboardName = dashboardData.Name || dashboardData.name || "Untitled Dashboard";
      this.originalDashboardName = this.dashboardName; // Set original name
      this.applyDashboardMeta(dashboardData);
      
      // Set AI response content and seed dashboard chat history (session-only)
      if (dashboardData.content) {
        this.aiResponse = dashboardData.content;
        this.ensureChatHistory("dashboard");
        if (this.initialMessage && !this.chatHistories.dashboard.some((m) => m.role === "user" && m.text === this.initialMessage)) {
          this.chatHistories.dashboard.push({ role: "user", text: this.initialMessage });
        }
        this.chatHistories.dashboard.push({ role: "ai", text: dashboardData.content });
      }

      if (dashboardData.ChartsID && dashboardData.ChartsID.length > 0) {
        await this.loadChartsFromIds(dashboardData.ChartsID);
      }
    },
    getChartLoadErrorMessage(error, fallbackTitle = "Plot") {
      const apiMessage = error?.response?.data?.message;
      if (apiMessage) {
        if (String(apiMessage).includes("don't have permission")) {
          return `${fallbackTitle}: ${apiMessage}`;
        }
        return apiMessage;
      }
      return error?.message || `Failed to load ${fallbackTitle}`;
    },
    withTimeout(promise, ms, timeoutMessage) {
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), ms);
      });
      return Promise.race([promise, timeoutPromise]).finally(() => {
        clearTimeout(timeoutId);
      });
    },
    async loadSingleChart(chartId, index) {
      const fallbackTitle = `Plot ${index + 1}`;
      try {
        const chartResponse = await this.withTimeout(
          api.get(`public/api/charts/${chartId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }),
          45000,
          `Timed out while loading ${fallbackTitle}`
        );

        if (
          !chartResponse?.data ||
          (chartResponse.data.statusCode !== 200 && chartResponse.data.statusCode !== 201)
        ) {
          throw new Error(chartResponse?.data?.message || `Failed to load ${fallbackTitle}`);
        }

        const chart = chartResponse.data.data?.chart || chartResponse.data.data;
        if (!chart) {
          throw new Error(`No data returned for ${fallbackTitle}`);
        }

        const chartTitle = chart.Name || fallbackTitle;

        try {
          const filterParams = this.buildFilterQueryParams(chart.Filter);
          const queryString = Object.entries(filterParams)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join("&");
          const url = queryString
            ? `public/api/charts/data/${chartId}?${queryString}`
            : `public/api/charts/data/${chartId}`;

          const chartDataResponse = await this.withTimeout(
            api.get(url, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            }),
            45000,
            `Timed out while loading data for ${chartTitle}`
          );

          let chartData = null;
          if (
            chartDataResponse?.data &&
            (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)
          ) {
            const samples = chartDataResponse.data.data?.samples;
            if (samples != null) {
              if (Array.isArray(samples) && samples.length > 0) {
                chartData = samples;
              } else if (typeof samples === "object" && "rows" in samples && samples.rows) {
                chartData = samples.rows;
              }
            }
          }

          if (!chartData || !chartData.length) {
            return {
              title: chartTitle,
              error: `Data is not available for this plot (${chartTitle})`,
              index,
            };
          }

          return {
            title: chartTitle,
            data: chart,
            chartData,
            index,
          };
        } catch (dataError) {
          console.error(`Error loading chart data for ${chartId}:`, dataError);
          return {
            title: chartTitle,
            error: this.getChartLoadErrorMessage(dataError, chartTitle),
            index,
          };
        }
      } catch (error) {
        console.error(`Error loading chart ${chartId}:`, error);
        return {
          title: fallbackTitle,
          error: this.getChartLoadErrorMessage(error, fallbackTitle),
          index,
        };
      }
    },
    async loadChartsFromIds(chartIds) {
      const nextCharts = Array(6).fill(null);
      const ids = (chartIds || []).slice(0, 6);

      ids.forEach((chartId, i) => {
        nextCharts[i] = {
          title: "Loading plot...",
          loading: true,
          chartId,
          index: i,
        };
      });
      this.charts = nextCharts;

      await Promise.all(
        ids.map(async (chartId, i) => {
          const loadedChart = await this.loadSingleChart(chartId, i);
          this.charts.splice(i, 1, loadedChart);
        })
      );
    },
    createDashboardFromMessage(message) {
      console.log("Creating dashboard from message:", message);
    },
    deselectPlot() {
      // Deselect the plot and return to grid view
      this.selectedChartIndex = null;
      this.showVisualEditPanel = false;
      this.chatVisible = true; // Show chat again
    },
    getChartTitle(index) {
      // Get chart title with fallback
      if (this.charts[index]) {
        return this.charts[index].title ||
               this.charts[index].data?.Name ||
               `Plot ${index + 1}`;
      }
      return `Plot ${index + 1}`;
    },
    isSharedPublicPlot(index) {
      const chart = this.charts[index];
      if (!chart) return false;
      const metadata = chart.data || chart;
      const accessLevel = String(metadata.AccessLevel || "").toLowerCase();
      if (accessLevel !== "public") return false;
      const creator = metadata.CreatedBy || metadata.createdBy || "";
      const userEmail = this.getUser?.username || "";
      return Boolean(userEmail && creator && creator !== userEmail);
    },
    handleMessageLike(index) {
      console.log("Message liked:", index);
    },
    handleMessageDislike(index) {
      console.log("Message disliked:", index);
    },
    /**
     * When a single plot is open, handle axis zoom / reset commands locally (no LLM).
     * Examples: "reset zoom", "x 0-100", "zoom y 1 10", "cut plot x 0 50 y 0 20"
     */
    tryApplySinglePlotViewChatCommand(message, chatKey) {
      if (this.selectedChartIndex === null || !this.charts[this.selectedChartIndex]) {
        return false;
      }
      const spec = parsePlotViewZoomChatMessage(message);
      if (!spec) {
        return false;
      }
      const card = this.$refs.selectedPlotChartCard;
      if (!card || typeof card.applyViewFromChat !== "function") {
        return false;
      }
      if (!card.applyViewFromChat(spec)) {
        this.appendToChatHistory(chatKey, null, {
          role: "ai",
          text: "Could not change the plot view. Try again after the chart finishes loading.",
        });
        return true;
      }
      this.appendToChatHistory(chatKey, null, {
        role: "ai",
        text: describePlotViewZoomReply(spec),
      });
      return true;
    },
    async handleChatMessage(message) {
      console.log("New chat message:", message);
      console.log("Current dashboard ID:", this.currentDashboardId);
      
      const llmUrl = process.env.VUE_APP_LLM_URL;
      if (!llmUrl) {
        console.error("VUE_APP_LLM_URL is not defined in .env");
        this.$store.commit('api/showSnackbar', {
          message: "LLM URL is not configured",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      this.loadingChatMessage = true;
      const chatKey = this.currentChatKey;
      this.appendToChatHistory(chatKey, { role: "user", text: message }, null);

      if (this.tryApplySinglePlotViewChatCommand(message, chatKey)) {
        this.loadingChatMessage = false;
        return;
      }

      try {
        // Check if a plot is selected for edit (when selectedChartIndex is not null, use new API)
        if (this.selectedChartIndex !== null && this.charts[this.selectedChartIndex]) {
          // Use new API for visual edit mode
          const chart = this.charts[this.selectedChartIndex];
          const chartMetadata = chart.data || chart;
          const chartId = chartMetadata.ID;

          if (!chartId) {
            this.$store.commit('api/showSnackbar', {
              message: "Cannot update: Chart ID not found",
              color: 'error',
              timeout: 5000
            });
            this.loadingChatMessage = false;
            return;
          }

          // Build payload in the required format for LLM API
          const llmPayload = {
            data: {
              chart: {
                AccessLevel: chartMetadata.AccessLevel || "public",
                ChartType: chartMetadata.ChartType || "",
                CreatedBy: chartMetadata.CreatedBy || "",
                CreatedOn: chartMetadata.CreatedOn || new Date().toISOString(),
                Filter: chartMetadata.Filter || {},
                GroupColumn: chartMetadata.GroupColumn || "",
                ID: chartId,
                Name: chartMetadata.Name || "",
                TableType: chartMetadata.TableType || "",
                XAxisColumn: chartMetadata.XAxisColumn || "",
                XAxisScale: chartMetadata.XAxisScale || "linear",
                YAxisColumn: chartMetadata.YAxisColumn || "",
                YAxisScale: chartMetadata.YAxisScale || "linear",
                ZAxisColumn: chartMetadata.ZAxisColumn || "",
              }
            },
            page: "VisualEdit",
            message: message
          };

          const response = await api.post(
            `${llmUrl}api/updateChartInDashboard`,
            llmPayload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            }
          );

          if (response && response.data) {
            console.log("Visual Edit API response:", response.data);
            
            // Update AI response - check multiple possible response structures
            let content = null;
            if (response.data.content) {
              content = response.data.content;
            } else if (response.data.data && response.data.data.content) {
              content = response.data.data.content;
            } else if (response.data.message) {
              content = response.data.message;
            } else if (typeof response.data === 'string') {
              content = response.data;
            }
            
            if (content) {
              console.log("Setting AI response:", content);
              this.aiResponse = content;
              this.appendToChatHistory(chatKey, null, { role: "ai", text: content });
            } else {
              console.warn("No content found in API response:", response.data);
            }

            // Check if metadata.route = "chartEditAgent" - if so, reload chart from API
            if (response.data.metadata && response.data.metadata.route === "chartEditAgent") {
              console.log("ChartEditAgent route detected, reloading chart from API");
              // Reload the chart metadata and data from API
              await this.reloadSingleChart(this.selectedChartIndex);
            } else if (response.data.data && response.data.data.chart) {
              // If the response contains updated chart data, reload it
              const updatedChartMetadata = response.data.data.chart;
              this.charts[this.selectedChartIndex] = {
                ...this.charts[this.selectedChartIndex],
                data: updatedChartMetadata,
              };
              // Reload chart data
              await this.reloadChartData(this.selectedChartIndex);
            }

            this.$store.commit('api/showSnackbar', {
              message: "Plot updated successfully",
              color: 'success',
              timeout: 3000
            });
          }
        } else {
          // Use old API for normal chat mode
          // Get current charts IDs from the charts array
          const chartsID = this.charts
            .filter(chart => chart && chart.data && chart.data.ID)
            .map(chart => chart.data.ID);

          // Build full dashboard data object
          const dashboardData = {
            ID: this.currentDashboardId,
            Name: this.dashboardName,
            ChartsID: chartsID,
            AccessLevel: this.dashboardAccessLevel,
            isDefault: this.isDefault,
          };

          const requestPayload = {
            message: message,
          };

          // Add full dashboard data instead of just dashboard_id
          if (this.currentDashboardId) {
            requestPayload.dashboard = dashboardData;
          }

          const response = await api.post(
            `${llmUrl}api/dashboard`,
            requestPayload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            }
          );

          if (response && response.data) {
            const dashboardData = response.data;
            
            console.log("Dashboard API response:", dashboardData);
            
            // Update AI response - check multiple possible response structures
            let content = null;
            if (dashboardData.content) {
              content = dashboardData.content;
            } else if (dashboardData.data && dashboardData.data.content) {
              content = dashboardData.data.content;
            } else if (dashboardData.message) {
              content = dashboardData.message;
            } else if (typeof dashboardData === 'string') {
              content = dashboardData;
            }
            
            if (content) {
              console.log("Setting AI response:", content);
              this.aiResponse = content;
              this.appendToChatHistory(chatKey, null, { role: "ai", text: content });
            } else {
              console.warn("No content found in API response:", dashboardData);
            }

            // Update charts if new charts are provided
            if (dashboardData.ChartsID && dashboardData.ChartsID.length > 0) {
              await this.loadChartsFromIds(dashboardData.ChartsID);
              // Show success message when charts are updated
              this.$store.commit('api/showSnackbar', {
                message: "Dashboard updated successfully",
                color: 'success',
                timeout: 3000
              });
            }

            // Update dashboard name if provided
            if (dashboardData.Name) {
              this.dashboardName = dashboardData.Name;
              this.originalDashboardName = dashboardData.Name; // Update original name
              // Update URL with new dashboard name
              // Vue Router will handle encoding automatically
              this.$router.replace({
                name: "AgenticDashboardView",
                params: { dashboardName: this.dashboardName },
              });
            }
          } else {
            console.error("Invalid response from API");
            this.$store.commit('api/showSnackbar', {
              message: "Failed to process message",
              color: 'error',
              timeout: 5000
            });
          }
        }
      } catch (error) {
        console.error("Error sending chat message:", error);
        
        // Get plot name for error message if in edit mode
        let errorMessage = error.response?.data?.message || "Failed to send message";
        if (this.selectedChartIndex !== null && this.charts[this.selectedChartIndex]) {
          const chart = this.charts[this.selectedChartIndex];
          const chartMetadata = chart.data || chart;
          const plotName = chartMetadata.Name || "Plot";
          
          // Check for permission error (400 status with permission message)
          if (error.response?.data?.statusCode === 400 && 
              error.response?.data?.message && 
              error.response.data.message.includes("don't have permission")) {
            errorMessage = `${plotName}: ${error.response.data.message}`;
          } else {
            errorMessage = `${plotName}: ${errorMessage}`;
          }
        }
        
        this.$store.commit('api/showSnackbar', {
          message: errorMessage,
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.loadingChatMessage = false;
      }
    },
    handleDashboardNameChange() {
      if (!this.isDashboardCreator) {
        return;
      }
      console.log("Dashboard name changed:", this.dashboardName);
    },
    toggleChat() {
      this.chatVisible = !this.chatVisible;
    },
    ensureChatHistory(key) {
      if (!key) return;
      if (!Array.isArray(this.chatHistories[key])) {
        this.chatHistories = { ...this.chatHistories, [key]: [] };
      }
    },
    appendToChatHistory(key, userMsg, aiMsg) {
      if (!key) return;
      this.ensureChatHistory(key);
      const list = this.chatHistories[key];
      if (userMsg) list.push(userMsg);
      if (aiMsg) list.push(aiMsg);
    },
    resizeAllCharts() {
      this.$nextTick(() => {
        eventBus.emit("resizeChart");
        const chartContainers = document.querySelectorAll(".chart-container");
        chartContainers.forEach((container) => {
          const plotElement =
            container.querySelector(".js-plotly-plot") ||
            (container.classList.contains("js-plotly-plot") ? container : null);
          if (!plotElement) {
            return;
          }
          try {
            Plotly.Plots.resize(plotElement).catch((err) => {
              console.warn("Error resizing chart:", err);
            });
          } catch (error) {
            console.warn("Error resizing chart:", error);
          }
        });
      });
    },
    handleEmptySlotClick() {
      if (!this.chatVisible) {
        this.chatVisible = true;
        this.$nextTick(() => {
          setTimeout(() => {
            this.focusChatInput();
          }, 350); // Wait for CSS transition
        });
      } else {
        this.focusChatInput();
      }
    },
    focusChatInput() {
      if (this.$refs.chatSidebar) {
        this.$refs.chatSidebar.focusInput();
      }
    },
    handleChartSelected(index) {
      const chart = this.charts[index];
      if (!chart || chart.loading || chart.error) {
        return;
      }
      // Select chart - load it on the left screen
      if (this.selectedChartIndex === index) {
        // Deselect if clicking the same chart
        this.selectedChartIndex = null;
        this.showVisualEditPanel = false;
        this.chatVisible = true;
      } else {
        this.selectedChartIndex = index;
        // Keep chat visible to show visual controls
        this.chatVisible = true;
        this.showVisualEditPanel = false;
      }
    },
    handleVisualEdit() {
      if (!this.isDashboardCreator) {
        return;
      }
      if (this.selectedChartIndex === null || !this.charts[this.selectedChartIndex]) {
        // No plot selected - show message
        this.$store.commit('api/showSnackbar', {
          message: "Please select a plot first",
          color: 'warning',
          timeout: 3000
        });
        this.showVisualEditPanel = false;
        this.editMode = false;
      } else {
        // Plot selected - show visual edit panel (replaces chat)
        this.showVisualEditPanel = true;
        this.chatVisible = false; // Hide chat when showing visual edit
      }
    },
    switchToChat() {
      // Switch from visual edit back to chat
      this.showVisualEditPanel = false;
      this.chatVisible = true;
    },
    exitEditMode() {
      // Exit visual edit and return to chat/dashboard view
      this.showVisualEditPanel = false;
      this.chatVisible = true; // Show chat again
      // Optionally deselect the chart
      // this.selectedChartIndex = null;
    },
    normalizeYAxisColumnValue(value) {
      if (!value) return "";
      if (Array.isArray(value)) {
        return value.filter((col) => col && String(col).trim()).join(",");
      }
      if (typeof value === "string") {
        return value.trim();
      }
      return String(value);
    },
    buildYAxisColumnsFromMetadata(chartMetadata, updateData) {
      const groupColumn =
        updateData.GroupColumn !== undefined
          ? updateData.GroupColumn || ""
          : chartMetadata.GroupColumn || "";

      let yAxisColumns = [];
      if (Array.isArray(updateData.YAxisColumns) && updateData.YAxisColumns.length) {
        yAxisColumns = updateData.YAxisColumns.map((col) => String(col).trim()).filter(Boolean);
      } else if (updateData.YAxisColumn !== undefined) {
        yAxisColumns = this.normalizeYAxisColumnValue(updateData.YAxisColumn)
          .split(",")
          .map((col) => col.trim())
          .filter(Boolean);
      } else if (Array.isArray(chartMetadata.YAxisColumns) && chartMetadata.YAxisColumns.length) {
        yAxisColumns = chartMetadata.YAxisColumns.map((col) => String(col).trim()).filter(Boolean);
      } else if (chartMetadata.YAxisColumn) {
        yAxisColumns = this.normalizeYAxisColumnValue(chartMetadata.YAxisColumn)
          .split(",")
          .map((col) => col.trim())
          .filter(Boolean);
      }

      if (groupColumn) {
        yAxisColumns = yAxisColumns.filter((col) => col !== groupColumn);
        yAxisColumns.push(groupColumn);
      }

      const yAxisColumnString = yAxisColumns
        .filter((col) => col !== groupColumn)
        .join(",");

      return { yAxisColumns, yAxisColumnString, groupColumn };
    },
    buildVisualEditChartPayload(chartMetadata, updateData) {
      const { yAxisColumns, yAxisColumnString, groupColumn } =
        this.buildYAxisColumnsFromMetadata(chartMetadata, updateData);

      const payload = {
        ID: chartMetadata.ID,
        name: updateData.Name ?? chartMetadata.Name ?? chartMetadata.name ?? "",
        ChartType: updateData.ChartType ?? chartMetadata.ChartType ?? "",
        TableType: updateData.TableType ?? chartMetadata.TableType ?? "",
        XAxisColumn: updateData.XAxisColumn ?? chartMetadata.XAxisColumn ?? "",
        AccessLevel: chartMetadata.AccessLevel || "public",
        Filter: updateData.Filter ?? chartMetadata.Filter ?? {},
        XAxisScale: updateData.XAxisScale ?? chartMetadata.XAxisScale ?? "linear",
        YAxisScale: updateData.YAxisScale ?? chartMetadata.YAxisScale ?? "linear",
        GroupColumn: groupColumn,
        ZAxisColumn: updateData.ZAxisColumn ?? chartMetadata.ZAxisColumn ?? "",
      };

      if (yAxisColumns.length > 0) {
        payload.YAxisColumns = yAxisColumns;
      }

      if (updateData.NumXBins !== undefined && updateData.NumXBins !== null) {
        payload.NumXBins = updateData.NumXBins;
      } else if (chartMetadata.NumXBins !== undefined && chartMetadata.NumXBins !== null) {
        payload.NumXBins = chartMetadata.NumXBins;
      }
      if (updateData.NumYBins !== undefined && updateData.NumYBins !== null) {
        payload.NumYBins = updateData.NumYBins;
      } else if (chartMetadata.NumYBins !== undefined && chartMetadata.NumYBins !== null) {
        payload.NumYBins = chartMetadata.NumYBins;
      }

      return { payload, yAxisColumns, yAxisColumnString };
    },
    mergeVisualEditChartMetadata(chartMetadata, apiChart, chartId, sentPayload, yAxisColumns, yAxisColumnString) {
      const merged = {
        ...chartMetadata,
        ...(apiChart || {}),
        ID: chartId,
        Name: apiChart?.Name || apiChart?.name || sentPayload.name || chartMetadata.Name || chartMetadata.name || "",
        Filter: sentPayload.Filter ?? apiChart?.Filter ?? chartMetadata.Filter ?? {},
        XAxisColumn: sentPayload.XAxisColumn ?? apiChart?.XAxisColumn ?? chartMetadata.XAxisColumn ?? "",
        YAxisColumn: yAxisColumnString,
        YAxisColumns: yAxisColumns,
        GroupColumn: sentPayload.GroupColumn ?? apiChart?.GroupColumn ?? chartMetadata.GroupColumn ?? "",
        ZAxisColumn: sentPayload.ZAxisColumn ?? apiChart?.ZAxisColumn ?? chartMetadata.ZAxisColumn ?? "",
        ChartType: sentPayload.ChartType ?? apiChart?.ChartType ?? chartMetadata.ChartType ?? "",
        TableType: sentPayload.TableType ?? apiChart?.TableType ?? chartMetadata.TableType ?? "",
        XAxisScale: sentPayload.XAxisScale ?? apiChart?.XAxisScale ?? chartMetadata.XAxisScale ?? "linear",
        YAxisScale: sentPayload.YAxisScale ?? apiChart?.YAxisScale ?? chartMetadata.YAxisScale ?? "linear",
      };

      if (sentPayload.NumXBins !== undefined && sentPayload.NumXBins !== null) {
        merged.NumXBins = sentPayload.NumXBins;
      } else if (chartMetadata.NumXBins !== undefined && merged.NumXBins === undefined) {
        merged.NumXBins = chartMetadata.NumXBins;
      }
      if (sentPayload.NumYBins !== undefined && sentPayload.NumYBins !== null) {
        merged.NumYBins = sentPayload.NumYBins;
      } else if (chartMetadata.NumYBins !== undefined && merged.NumYBins === undefined) {
        merged.NumYBins = chartMetadata.NumYBins;
      }

      return merged;
    },
    async fetchChartSamples(chartId, chartFilter) {
      if (!chartId) return null;

      const filterParams = this.buildFilterQueryParams(chartFilter);
      const queryString = Object.entries(filterParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");

      const url = queryString
        ? `public/api/charts/data/${chartId}?${queryString}`
        : `public/api/charts/data/${chartId}`;

      const chartDataResponse = await api.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-TenantID": this.tenantId,
        },
      });

      if (
        chartDataResponse &&
        chartDataResponse.data &&
        (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)
      ) {
        const samples = chartDataResponse.data.data?.samples;
        if (samples != null) {
          if (Array.isArray(samples)) {
            return samples;
          }
          if (typeof samples === "object" && "rows" in samples && samples.rows) {
            return samples.rows;
          }
        }
      }

      return null;
    },
    async handlePlotUpdated(updateData) {
      if (!this.isDashboardCreator) {
        return;
      }
      if (this.selectedChartIndex === null || !this.charts[this.selectedChartIndex]) {
        return;
      }

      const chartIndex = this.selectedChartIndex;
      const chart = this.charts[chartIndex];
      const chartMetadata = chart.data || chart;
      const chartId = chartMetadata.ID;

      if (!chartId) {
        this.$store.commit('api/showSnackbar', {
          message: "Cannot update: Chart ID not found",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      try {
        const { payload: chartPayload, yAxisColumns, yAxisColumnString } =
          this.buildVisualEditChartPayload(chartMetadata, updateData);

        const updatePayload = {
          data: {
            chart: chartPayload,
          },
        };

        const response = await api.put(`public/api/charts/${chartId}`, updatePayload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          const updatedChartMetadata = response.data.data?.chart || response.data.data;
          const finalMetadata = this.mergeVisualEditChartMetadata(
            chartMetadata,
            updatedChartMetadata,
            chartId,
            chartPayload,
            yAxisColumns,
            yAxisColumnString
          );

          const chartData = await this.fetchChartSamples(chartId, finalMetadata.Filter);

          this.charts[chartIndex] = {
            ...this.charts[chartIndex],
            data: finalMetadata,
            chartData,
            title: finalMetadata.Name || chart.title || `Chart ${chartIndex + 1}`,
          };

          this.$store.commit('api/showSnackbar', {
            message: "Plot updated successfully",
            color: 'success',
            timeout: 3000
          });
        } else {
          const plotName = updateData.Name || chartMetadata.Name || "Plot";
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${response?.data?.message || "Failed to update plot"}`,
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error updating plot:", error);

        const plotName = updateData.Name || chartMetadata.Name || "Plot";

        if (error.response?.data?.statusCode === 400 &&
            error.response?.data?.message &&
            error.response.data.message.includes("don't have permission")) {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response.data.message}`,
            color: 'error',
            timeout: 5000
          });
        } else {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response?.data?.message || "Failed to update plot"}`,
            color: 'error',
            timeout: 5000
          });
        }
      }
    },
    async reloadSingleChart(chartIndex) {
      // Reload a single chart from API (metadata + data) without refreshing other data
      const chart = this.charts[chartIndex];
      if (!chart) return;
      
      const chartMetadata = chart.data || chart;
      const chartId = chartMetadata.ID;

      if (!chartId) {
        console.warn("Cannot reload chart: Chart ID not found");
        return;
      }

      try {
        // Get updated chart metadata
        const chartResponse = await api.get(`public/api/charts/${chartId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          chartResponse &&
          chartResponse.data &&
          (chartResponse.data.statusCode === 200 || chartResponse.data.statusCode === 201)
        ) {
          const updatedChart = chartResponse.data.data?.chart || chartResponse.data.data;
          
          // Get updated chart data with filters
          try {
            // Build filter query parameters from chart Filter object
            const filterParams = this.buildFilterQueryParams(updatedChart.Filter);
            const queryString = Object.entries(filterParams)
              .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
              .join("&");
            
            // Build URL with filter query parameters if any exist
            const url = queryString 
              ? `public/api/charts/data/${chartId}?${queryString}`
              : `public/api/charts/data/${chartId}`;
            
            const chartDataResponse = await api.get(url, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                "X-TenantID": this.tenantId,
              },
            });

            let chartData = null;
            if (
              chartDataResponse &&
              chartDataResponse.data &&
              (chartDataResponse.data.statusCode === 200 || chartDataResponse.data.statusCode === 201)
            ) {
              const samples = chartDataResponse.data.data?.samples;
              
              if (samples != null) {
                if (Array.isArray(samples)) {
                  chartData = samples;
                } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
                  chartData = samples.rows;
                }
              }
            }

            // Update only this chart in the array - this will trigger re-render
            this.charts[chartIndex] = {
              title: updatedChart.Name || chart.title || `Chart ${chartIndex + 1}`,
              data: updatedChart,
              chartData: chartData,
              index: chartIndex,
            };

            console.log("Chart reloaded successfully:", {
              chartIndex,
              chartId,
              chartName: updatedChart.Name
            });
          } catch (dataError) {
            console.error("Error reloading chart data:", dataError);
            // Still update metadata even if data fails
            this.charts[chartIndex] = {
              ...this.charts[chartIndex],
              data: updatedChart,
            };
          }
        }
      } catch (error) {
        console.error("Error reloading single chart:", error);
        
        const plotName = chartMetadata?.Name || "Plot";
        
        // Check for permission error
        if (error.response?.data?.statusCode === 400 && 
            error.response?.data?.message && 
            error.response.data.message.includes("don't have permission")) {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response.data.message}`,
            color: 'error',
            timeout: 5000
          });
        }
      }
    },
    // Helper method to build filter query parameters from chart Filter object
    buildFilterQueryParams(chartFilter) {
      const queryParams = {};
      
      if (!chartFilter) return queryParams;
      
      // Only add filters to params if they have values
      if (chartFilter.asset && Array.isArray(chartFilter.asset) && chartFilter.asset.length) {
        queryParams.asset = JSON.stringify(chartFilter.asset);
      }
      if (chartFilter.field && Array.isArray(chartFilter.field) && chartFilter.field.length) {
        queryParams.field = JSON.stringify(chartFilter.field);
      }
      if (chartFilter.well && Array.isArray(chartFilter.well) && chartFilter.well.length) {
        queryParams.well = JSON.stringify(chartFilter.well);
      }
      if (chartFilter.reservoir && Array.isArray(chartFilter.reservoir) && chartFilter.reservoir.length) {
        queryParams.reservoir = JSON.stringify(chartFilter.reservoir);
      }
      if (chartFilter.fluidType && Array.isArray(chartFilter.fluidType) && chartFilter.fluidType.length) {
        queryParams.fluidType = JSON.stringify(chartFilter.fluidType);
      }
      if (chartFilter.sampleType && Array.isArray(chartFilter.sampleType) && chartFilter.sampleType.length) {
        queryParams.sampleType = JSON.stringify(chartFilter.sampleType);
      }
      if (chartFilter.fluidClassification && Array.isArray(chartFilter.fluidClassification) && chartFilter.fluidClassification.length) {
        queryParams.fluidClassification = JSON.stringify(chartFilter.fluidClassification);
      }
      if (chartFilter.fluidSampleID && Array.isArray(chartFilter.fluidSampleID) && chartFilter.fluidSampleID.length) {
        queryParams.fluidSampleID = JSON.stringify(chartFilter.fluidSampleID);
      }
      if (chartFilter.fluidSampleContainerID && Array.isArray(chartFilter.fluidSampleContainerID) && chartFilter.fluidSampleContainerID.length) {
        queryParams.fluidSampleContainerID = JSON.stringify(chartFilter.fluidSampleContainerID);
      }
      if (chartFilter.source && chartFilter.source !== "") {
        queryParams.source = `"${chartFilter.source}"`;
      }
      if (chartFilter.depth) {
        queryParams.depth = JSON.stringify(chartFilter.depth);
      }
      if (chartFilter.formationPressure) {
        queryParams.formationPressure = JSON.stringify(chartFilter.formationPressure);
      }
      if (chartFilter.formationTemperature) {
        queryParams.formationTemperature = JSON.stringify(chartFilter.formationTemperature);
      }
      if (chartFilter.sampleDate) {
        queryParams.sampleDate = JSON.stringify(chartFilter.sampleDate);
      }
      
      return queryParams;
    },
    async reloadChartData(chartIndex) {
      const chart = this.charts[chartIndex];
      const chartMetadata = chart.data || chart;
      const chartId = chartMetadata.ID;

      if (!chartId) return;

      try {
        const chartData = await this.fetchChartSamples(chartId, chartMetadata.Filter);

        this.charts[chartIndex] = {
          ...this.charts[chartIndex],
          chartData,
        };
      } catch (error) {
        console.error("Error reloading chart data:", error);
        
        // Get plot name for error message
        const chart = this.charts[chartIndex];
        const chartMetadata = chart?.data || chart;
        const plotName = chartMetadata?.Name || "Plot";
        
        // Check for permission error (400 status with permission message)
        if (error.response?.data?.statusCode === 400 && 
            error.response?.data?.message && 
            error.response.data.message.includes("don't have permission")) {
          this.$store.commit('api/showSnackbar', {
            message: `${plotName}: ${error.response.data.message}`,
            color: 'error',
            timeout: 5000
          });
        }
      }
    },
    handleDashAccessToggleChange() {
      if (!this.isDashboardCreator || !this.currentDashboardId) return;

      const previousLevel = this.dashboardAccessLevel;
      const previousPublic = this.isPublic;
      this.dashboardAccessLevel = this.isPublic ? "public" : "private";

      api
        .put(
          `public/api/dashboards/${this.currentDashboardId}`,
          {
            Data: {
              dashboard: {
                name: this.dashboardName.trim(),
                AccessLevel: this.dashboardAccessLevel,
              },
            },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        )
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            this.$store.commit("api/showSnackbar", {
              message: this.isPublic
                ? "Dashboard is now shared"
                : "Dashboard is now private",
              color: "success",
              timeout: 3000,
            });
          } else {
            this.dashboardAccessLevel = previousLevel;
            this.isPublic = previousPublic;
            this.$store.commit("api/showSnackbar", {
              message: response?.data?.message || "Failed to update dashboard visibility",
              color: "error",
              timeout: 5000,
            });
          }
        })
        .catch((error) => {
          console.error("Error updating dashboard access level:", error);
          this.dashboardAccessLevel = previousLevel;
          this.isPublic = previousPublic;
          this.$store.commit("api/showSnackbar", {
            message: error.response?.data?.message || "Error updating dashboard visibility",
            color: "error",
            timeout: 5000,
          });
        });
    },
    async saveDashboard() {
      if (!this.isDashboardCreator) {
        return;
      }
      if (!this.currentDashboardId) {
        this.$store.commit('api/showSnackbar', {
          message: "Cannot save: Dashboard ID not found",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      if (!this.isDashboardNameChanged) {
        return; // No changes to save
      }

      this.saving = true;

      try {
        const chartsID = this.charts
          .filter(chart => chart && chart.data && chart.data.ID)
          .map(chart => chart.data.ID);

        const requestBody = {
          Data: {
            dashboard: {
              name: this.dashboardName.trim(),
              chartsID: chartsID,
              AccessLevel: this.dashboardAccessLevel,
              isDefault: this.isDefault,
            },
          },
        };

        const response = await api.put(
          `public/api/dashboards/${this.currentDashboardId}`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          this.originalDashboardName = this.dashboardName.trim();
          
          this.$router.replace({
            name: "AgenticDashboardView",
            params: { dashboardName: this.dashboardName.trim() },
          });
          
          this.$store.commit('api/showSnackbar', {
            message: "Dashboard updated successfully",
            color: 'success',
            timeout: 3000
          });
        } else {
          const errorMessage = response?.data?.message || "Failed to update dashboard";
          console.error(errorMessage);
          this.$store.commit('api/showSnackbar', {
            message: errorMessage,
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error updating dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Error updating dashboard",
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete() {
      const dashboardId = this.currentDashboardId;
      if (!dashboardId) {
        this.$router.push({
          name: "AgenticDashboard",
        });
        return;
      }

      this.deleting = true;
      api
        .delete(`public/api/dashboards/${dashboardId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            this.$store.commit('api/showSnackbar', {
              message: "Dashboard deleted successfully",
              color: 'success',
              timeout: 3000
            });
            this.$router.push({
              name: "AgenticDashboard",
            });
          } else {
            this.$store.commit('api/showSnackbar', {
              message: response?.data?.message || "Failed to delete dashboard",
              color: 'error',
              timeout: 5000
            });
          }
        })
        .catch((error) => {
          console.error("Error deleting dashboard:", error);
          this.$store.commit('api/showSnackbar', {
            message: error.response?.data?.message || "Error deleting dashboard",
            color: 'error',
            timeout: 5000
          });
        })
        .finally(() => {
          this.deleting = false;
          this.showDeleteDialog = false;
        });
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard.css';
</style>

